// Helper functions to work with relational mock data
import { mockUsers } from './users';

// User lookup helpers
export const getUserById = (id: string) => {
  return mockUsers.find((user) => user.id === id);
};

export const getUsersByIds = (ids: string[]) => {
  return mockUsers.filter((user) => ids.includes(user.id));
};

export const getUsersByDepartment = (department: string) => {
  return mockUsers.filter((user) => user.department === department);
};

export const getUsersByRole = (role: string) => {
  return mockUsers.filter((user) => user.role === role);
};

// Project lookup helpers (will be populated when projects.ts is created)
export const getProjectById = (id: string, projects: any[]) => {
  return projects.find((project: any) => project.id === id);
};

export const getProjectsByOwnerId = (ownerId: string, projects: any[]) => {
  return projects.filter((project: any) => project.ownerId === ownerId);
};

export const getProjectsByMemberId = (memberId: string, projects: any[]) => {
  return projects.filter((project: any) =>
    project.memberIds && project.memberIds.includes(memberId)
  );
};

// Task lookup helpers (will be populated when tasks.ts is created)
export const getTaskById = (id: string, tasks: any[]) => {
  return tasks.find((task: any) => task.id === id);
};

export const getTasksByProjectId = (projectId: string, tasks: any[]) => {
  return tasks.filter((task: any) => task.projectId === projectId);
};

export const getTasksByAssigneeId = (assigneeId: string, tasks: any[]) => {
  return tasks.filter((task: any) => task.assigneeId === assigneeId);
};

export const getTasksByStatus = (status: string, tasks: any[]) => {
  return tasks.filter((task: any) => task.status === status);
};

// Generic filtering helpers
export const filterByDateRange = <T extends { createdAt?: Date; date?: Date }>(
  items: T[],
  startDate: Date,
  endDate: Date
): T[] => {
  return items.filter((item) => {
    const itemDate = item.createdAt || item.date;
    if (!itemDate) return false;
    return itemDate >= startDate && itemDate <= endDate;
  });
};

export const sortByDate = <T extends { createdAt?: Date; date?: Date }>(
  items: T[],
  order: 'asc' | 'desc' = 'desc'
): T[] => {
  return [...items].sort((a, b) => {
    const dateA = (a.createdAt || a.date)?.getTime() || 0;
    const dateB = (b.createdAt || b.date)?.getTime() || 0;
    return order === 'asc' ? dateA - dateB : dateB - dateA;
  });
};

// Random data generators for dynamic demos
export const getRandomUsers = (count: number) => {
  const shuffled = [...mockUsers].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

export const getRandomUser = () => {
  return mockUsers[Math.floor(Math.random() * mockUsers.length)];
};

// Format helpers
export const formatUserName = (userId: string) => {
  const user = getUserById(userId);
  return user?.name || 'Unknown User';
};

export const formatUserEmail = (userId: string) => {
  const user = getUserById(userId);
  return user?.email || '';
};

export const formatUserAvatar = (userId: string) => {
  const user = getUserById(userId);
  return user?.avatar;
};
